/**
 * The real Quoflow enquiry-status enum — a TRUE identity map, re-verified live 2026-08-04 against
 * `get_pipeline_analytics.by_stage`, which returns exactly:
 *
 *   Draft | Open | RFQ Sent | Quotes Received | Comparison Done | Quotation Sent
 *   | Won | Rejected | Archived
 *
 * `ENQUIRY_STATUSES` (src/lib/types.ts) IS this list — no renaming, no folding, both directions
 * agree by construction because there is only one direction now.
 *
 * CORRECTION (fix round 1): an earlier version of this file (and this task's own first pass)
 * mapped the terminal states as `Lost` → `Rejected`, `Cancelled` → `Archived`, sourced from a
 * 2026-08-01 comment against `docs/frappe-schema/quoflow-schema.json` (the superseded
 * `Opportunity` doctype dump). That enum belongs to a different transport than the one this file
 * actually talks to (`quoflow.enquiry.*`, verified separately above) — on THIS one the server
 * sends `Rejected`/`Archived` literally, never `Lost`/`Cancelled`. The old map therefore silently
 * routed a real `Rejected`/`Archived` enquiry into `Open` (via `toBoardStatus`'s fallback), which
 * also meant it inherited `Open`'s Kanban route (`rfq`, not the decide flow) and its "not yet
 * worked" board tone — a live-data bug this file's own test suite didn't catch, because the test
 * suite encoded the same wrong vocabulary. See docs/bosun-frappe-open-questions.md, Q1.
 */

import type { EnquiryStatus } from '../../types'

const VALID = new Set<string>([
  'Draft',
  'Open',
  'RFQ Sent',
  'Quotes Received',
  'Comparison Done',
  'Quotation Sent',
  'Won',
  'Rejected',
  'Archived',
])

/** An unrecognised or missing server status falls back to `Open` rather than inventing a tenth
 * board column or dropping the card — `Open` is the "just landed, not yet worked" state. */
export function toBoardStatus(serverStatus: unknown): EnquiryStatus {
  return typeof serverStatus === 'string' && VALID.has(serverStatus) ? (serverStatus as EnquiryStatus) : 'Open'
}

/**
 * Whether the server's raw status was one this client knows — i.e. whether `toBoardStatus` above
 * PASSED IT THROUGH or quietly folded it into `Open`.
 *
 * The fold is the right call for rendering (a card must still appear somewhere, and `Open` is the
 * safe column) but it is silent, and silence is what made it dangerous. `kanban.ts`'s `unplaced`
 * bucket and the board's "status this board doesn't recognise" banner were written to catch a
 * backend that added a tenth status — but they could never fire, because by the time
 * `groupIntoColumns` sees a card its status has already been folded to a value the board knows.
 * Every unrecognised enquiry piled into `Open` with no warning at all, looking exactly like a
 * card that had not been worked yet.
 *
 * So the board asks this of the RAW value (`EnquirySummary.status_raw`) instead. A card is still
 * placed in `Open` — visible beats correct-but-hidden — and additionally flagged, so the next
 * vocabulary drift announces itself rather than being mistaken for a missing card.
 *
 * `undefined` reads as known: callers that never set `status_raw` are not making a claim about
 * the server's vocabulary, and must not raise a false alarm.
 */
export function isKnownBoardStatus(rawStatus: string | undefined): boolean {
  return rawStatus === undefined || VALID.has(rawStatus)
}
